import { useState, useMemo, useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Autocomplete,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import type { Product, Location, Inventory, LocationType } from '../../types';

interface TransferItem {
  productId: string;
  quantity: number;
  notes?: string;
}

interface MultiProductFormData {
  fromLocationId: string;
  fromLocationType: LocationType;
  toLocationId: string;
  toLocationType: LocationType;
  notes: string;
  items: TransferItem[];
}

interface SingleProductFormData {
  productId: string;
  quantity: number;
  fromLocationId: string;
  fromLocationType: LocationType;
  toLocationId: string;
  toLocationType: LocationType;
  notes: string;
}

const fetchProducts = async (): Promise<Product[]> => {
  const response = await axiosInstance.get('/products');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchLocations = async (): Promise<Location[]> => {
  const response = await axiosInstance.get('/locations');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchInventory = async (locationId: string): Promise<Inventory[]> => {
  const response = await axiosInstance.get(`/inventory/location/${locationId}`);
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const steps = ['Transfer Details', 'Add Products', 'Review'];

export const StockTransferCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showMessage } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [isMultiProduct, setIsMultiProduct] = useState(true);
  const [prePopulated, setPrePopulated] = useState(false);
  // Track selected toLocation separately to prevent Autocomplete display issues
  const [selectedToLocation, setSelectedToLocation] = useState<{ id: string; name: string } | null>(null);

  // Parse URL parameters for pre-population
  const urlFromLocationId = searchParams.get('fromLocationId');
  const urlProductId = searchParams.get('productId');

  // Multi-product form
  const multiProductForm = useForm<MultiProductFormData>({
    defaultValues: {
      fromLocationId: '',
      fromLocationType: 'warehouse',
      toLocationId: '',
      toLocationType: 'store',
      notes: '',
      items: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: multiProductForm.control,
    name: 'items',
  });

  // Single-product form
  const singleProductForm = useForm<SingleProductFormData>({
    defaultValues: {
      productId: '',
      quantity: 1,
      fromLocationId: '',
      fromLocationType: 'warehouse',
      toLocationId: '',
      toLocationType: 'store',
      notes: '',
    },
  });

  const activeForm = isMultiProduct ? multiProductForm : singleProductForm;
  const fromLocationType = activeForm.watch('fromLocationType' as any) as unknown as LocationType;
  const toLocationType = activeForm.watch('toLocationType' as any) as unknown as LocationType;
  const fromLocationId = activeForm.watch('fromLocationId' as any) as unknown as string;

  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: fetchProducts,
  });

  const { data: locations = [], isLoading: locationsLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocations,
  });

  const { data: sourceInventory = [] } = useQuery({
    queryKey: ['inventory', fromLocationId],
    queryFn: () => fetchInventory(fromLocationId),
    enabled: !!fromLocationId,
  });

  // Pre-populate form from URL parameters
  useEffect(() => {
    if (prePopulated || locations.length === 0) return;
    if (!urlFromLocationId) return;

    // Find the source location to determine its type
    const sourceLocation = locations.find(l => l.id === urlFromLocationId);
    if (!sourceLocation) return;

    // Switch to single-product mode if productId is provided
    if (urlProductId) {
      setIsMultiProduct(false);
      singleProductForm.setValue('fromLocationType', sourceLocation.type);
      singleProductForm.setValue('fromLocationId', urlFromLocationId);
      singleProductForm.setValue('productId', urlProductId);
    } else {
      // Multi-product mode with just source location pre-populated
      multiProductForm.setValue('fromLocationType', sourceLocation.type);
      multiProductForm.setValue('fromLocationId', urlFromLocationId);
    }

    setPrePopulated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, urlFromLocationId, urlProductId, prePopulated]);

  // Pre-populate quantity when inventory is loaded (for single-product mode)
  useEffect(() => {
    if (!urlProductId || isMultiProduct || sourceInventory.length === 0) return;

    const inventory = sourceInventory.find(inv => inv.productId === urlProductId);
    if (inventory && inventory.quantity > 0) {
      const currentQty = singleProductForm.getValues('quantity');
      // Only set if still at default value of 1
      if (currentQty === 1) {
        singleProductForm.setValue('quantity', inventory.quantity);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceInventory, urlProductId, isMultiProduct]);

  type LocationOption = { id: string; name: string };
  const fromLocations: LocationOption[] = locations
    .filter(l => l.type === fromLocationType && l.canSendTransfers)
    .map(l => ({ id: l.id, name: l.name }));

  const toLocations: LocationOption[] = locations
    .filter(l => l.type === toLocationType && l.canReceiveTransfers)
    .map(l => ({ id: l.id, name: l.name }));

  // Get available quantity for a product at source location
  const getAvailableQuantity = (productId: string): number => {
    if (!fromLocationId || !productId) return 0;
    const inventory = sourceInventory.find(inv => inv.productId === productId);
    return inventory?.quantity || 0;
  };

  // Get products available at source location with stock > 0
  const availableProducts = useMemo(() => {
    if (!fromLocationId) return products;
    return products.filter(p => {
      const stock = getAvailableQuantity(p.id);
      return stock > 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, sourceInventory, fromLocationId]);

  const createMutation = useMutation({
    mutationFn: (data: MultiProductFormData | SingleProductFormData) =>
      axiosInstance.post('/stock-transfers', data),
    onSuccess: () => {
      showMessage('Stock transfer created successfully', 'success');
      navigate('/app/stock-transfers');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to create stock transfer', 'error');
    },
  });

  const handleNext = async () => {
    if (activeStep === 0) {
      // Ensure the toLocationId is synced from our local state before validation
      if (selectedToLocation) {
        if (isMultiProduct) {
          multiProductForm.setValue('toLocationId', selectedToLocation.id, { shouldValidate: false });
        } else {
          singleProductForm.setValue('toLocationId', selectedToLocation.id, { shouldValidate: false });
        }
      }

      // Validate transfer details
      const isValid = isMultiProduct
        ? await multiProductForm.trigger(['fromLocationId', 'toLocationId'])
        : await singleProductForm.trigger(['fromLocationId', 'toLocationId']);
      if (!isValid) return;

      // Check locations are different
      const fromId = isMultiProduct ? multiProductForm.getValues('fromLocationId') : singleProductForm.getValues('fromLocationId');
      const toId = isMultiProduct ? multiProductForm.getValues('toLocationId') : singleProductForm.getValues('toLocationId');
      if (fromId === toId) {
        showMessage('Source and destination locations must be different', 'error');
        return;
      }
    }

    if (activeStep === 1 && isMultiProduct) {
      // Validate items
      const items = multiProductForm.getValues('items');
      if (items.length === 0) {
        showMessage('Please add at least one product', 'error');
        return;
      }

      // Validate each item
      const isValid = await multiProductForm.trigger('items');
      if (!isValid) return;
    }

    if (activeStep === 1 && !isMultiProduct) {
      // Validate single product
      const isValid = await singleProductForm.trigger(['productId', 'quantity']);
      if (!isValid) return;
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = () => {
    if (isMultiProduct) {
      const formData = multiProductForm.getValues();
      // Strip UI-only fields that aren't part of the DTO
      const { fromLocationType, toLocationType, ...apiData } = formData;
      createMutation.mutate(apiData as any);
    } else {
      const formData = singleProductForm.getValues();
      // Strip UI-only fields that aren't part of the DTO
      const { fromLocationType, toLocationType, ...apiData } = formData;
      createMutation.mutate(apiData as any);
    }
  };

  const handleAddProduct = () => {
    append({ productId: '', quantity: 1, notes: '' });
  };

  const handleRemoveProduct = (index: number) => {
    remove(index);
  };

  const handleFromLocationTypeChange = (newType: LocationType) => {
    if (isMultiProduct) {
      multiProductForm.setValue('fromLocationType', newType);
      multiProductForm.setValue('fromLocationId', '');
    } else {
      singleProductForm.setValue('fromLocationType', newType);
      singleProductForm.setValue('fromLocationId', '');
    }
  };

  const handleToLocationTypeChange = (newType: LocationType) => {
    setSelectedToLocation(null);
    if (isMultiProduct) {
      multiProductForm.setValue('toLocationType', newType);
      multiProductForm.setValue('toLocationId', '');
    } else {
      singleProductForm.setValue('toLocationType', newType);
      singleProductForm.setValue('toLocationId', '');
    }
  };

  const toggleTransferMode = () => {
    setIsMultiProduct(!isMultiProduct);
    setActiveStep(0); // Reset to first step
  };

  // Calculate totals for review
  const getTotalQuantity = (): number => {
    if (isMultiProduct) {
      const items = multiProductForm.getValues('items');
      return items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    } else {
      return singleProductForm.getValues('quantity') || 0;
    }
  };

  const getTotalItems = (): number => {
    if (isMultiProduct) {
      return multiProductForm.getValues('items').length;
    } else {
      return 1;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Create Stock Transfer
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={isMultiProduct}
              onChange={toggleTransferMode}
              color="primary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isMultiProduct && <Inventory2Icon sx={{ fontSize: 20 }} />}
              <Typography variant="body2">
                {isMultiProduct ? 'Multi-Product Mode' : 'Single Product Mode'}
              </Typography>
            </Box>
          }
        />
      </Box>

      <Card>
        <CardContent>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 1: Transfer Details */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Transfer Route
              </Typography>
              <Divider sx={{ mb: 3 }} />

              {/* From Location Type */}
              <FormControl fullWidth margin="normal" error={!!(isMultiProduct ? multiProductForm.formState.errors.fromLocationType : singleProductForm.formState.errors.fromLocationType)}>
                <InputLabel>From Location Type</InputLabel>
                <Select
                  value={fromLocationType}
                  label="From Location Type"
                  onChange={(e) => handleFromLocationTypeChange(e.target.value as LocationType)}
                >
                  <MenuItem value="warehouse">Warehouse</MenuItem>
                  <MenuItem value="store">Store</MenuItem>
                </Select>
                {((isMultiProduct ? multiProductForm.formState.errors.fromLocationType : singleProductForm.formState.errors.fromLocationType)) && (
                  <FormHelperText>{(isMultiProduct ? multiProductForm.formState.errors.fromLocationType : singleProductForm.formState.errors.fromLocationType)?.message}</FormHelperText>
                )}
              </FormControl>

              {/* From Location */}
              {isMultiProduct ? (
                <Controller
                  name="fromLocationId"
                  control={multiProductForm.control}
                  rules={{ required: 'Source location is required' }}
                  render={({ field }) => (
                  <Autocomplete
                    options={fromLocations}
                    getOptionLabel={(option) =>
                      typeof option === 'string'
                        ? fromLocations.find((l) => l.id === option)?.name || ''
                        : option.name
                    }
                    value={fromLocations.find((l) => l.id === field.value) || null}
                    onChange={(_, newValue) => {
                      field.onChange(newValue?.id || '');
                    }}
                    onBlur={field.onBlur}
                    loading={locationsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`From ${fromLocationType === 'store' ? 'Store' : 'Warehouse'}`}
                        error={!!(isMultiProduct ? multiProductForm.formState.errors.fromLocationId : singleProductForm.formState.errors.fromLocationId)}
                        helperText={(isMultiProduct ? multiProductForm.formState.errors.fromLocationId : singleProductForm.formState.errors.fromLocationId)?.message}
                        margin="normal"
                        fullWidth
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {locationsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                  />
                )}
              />
              ) : (
                <Controller
                  name="fromLocationId"
                  control={singleProductForm.control}
                  rules={{ required: 'Source location is required' }}
                  render={({ field }) => (
                  <Autocomplete
                    options={fromLocations}
                    getOptionLabel={(option) =>
                      typeof option === 'string'
                        ? fromLocations.find((l) => l.id === option)?.name || ''
                        : option.name
                    }
                    value={fromLocations.find((l) => l.id === field.value) || null}
                    onChange={(_, newValue) => {
                      field.onChange(newValue?.id || '');
                    }}
                    onBlur={field.onBlur}
                    loading={locationsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`From ${fromLocationType === 'store' ? 'Store' : 'Warehouse'}`}
                        error={!!singleProductForm.formState.errors.fromLocationId}
                        helperText={singleProductForm.formState.errors.fromLocationId?.message}
                        margin="normal"
                        fullWidth
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {locationsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                  />
                )}
              />
              )}

              {/* To Location Type */}
              <FormControl fullWidth margin="normal" error={!!(isMultiProduct ? multiProductForm.formState.errors.toLocationType : singleProductForm.formState.errors.toLocationType)}>
                <InputLabel>To Location Type</InputLabel>
                <Select
                  value={toLocationType}
                  label="To Location Type"
                  onChange={(e) => handleToLocationTypeChange(e.target.value as LocationType)}
                >
                  <MenuItem value="store">Store</MenuItem>
                  <MenuItem value="warehouse">Warehouse</MenuItem>
                </Select>
                {((isMultiProduct ? multiProductForm.formState.errors.toLocationType : singleProductForm.formState.errors.toLocationType)) && (
                  <FormHelperText>{(isMultiProduct ? multiProductForm.formState.errors.toLocationType : singleProductForm.formState.errors.toLocationType)?.message}</FormHelperText>
                )}
              </FormControl>

              {/* To Location */}
              {isMultiProduct ? (
                <Controller
                  name="toLocationId"
                  control={multiProductForm.control}
                  rules={{ required: 'Destination location is required' }}
                  render={({ field }) => (
                    <Autocomplete
                      key={`to-location-multi-${toLocationType}`}
                      options={toLocations}
                      getOptionLabel={(option) =>
                        typeof option === 'string'
                          ? toLocations.find((l) => l.id === option)?.name || ''
                          : option.name
                      }
                      value={selectedToLocation}
                      onChange={(_, newValue) => {
                        setSelectedToLocation(newValue);
                        field.onChange(newValue?.id || '');
                      }}
                      onBlur={() => {
                        // Delay onBlur to allow onChange state update to complete
                        setTimeout(() => field.onBlur(), 0);
                      }}
                      loading={locationsLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`To ${toLocationType === 'store' ? 'Store' : 'Warehouse'}`}
                          error={!!multiProductForm.formState.errors.toLocationId}
                          helperText={multiProductForm.formState.errors.toLocationId?.message}
                          margin="normal"
                          fullWidth
                          required
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {locationsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                    />
                  )}
              />
              ) : (
                <Controller
                  name="toLocationId"
                  control={singleProductForm.control}
                  rules={{ required: 'Destination location is required' }}
                  render={({ field }) => (
                    <Autocomplete
                      key={`to-location-single-${toLocationType}`}
                      options={toLocations}
                      getOptionLabel={(option) =>
                        typeof option === 'string'
                          ? toLocations.find((l) => l.id === option)?.name || ''
                          : option.name
                      }
                      value={selectedToLocation}
                      onChange={(_, newValue) => {
                        setSelectedToLocation(newValue);
                        field.onChange(newValue?.id || '');
                      }}
                      onBlur={() => {
                        // Delay onBlur to allow onChange state update to complete
                        setTimeout(() => field.onBlur(), 0);
                      }}
                      loading={locationsLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`To ${toLocationType === 'store' ? 'Store' : 'Warehouse'}`}
                          error={!!singleProductForm.formState.errors.toLocationId}
                          helperText={singleProductForm.formState.errors.toLocationId?.message}
                          margin="normal"
                          fullWidth
                          required
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {locationsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                    />
                  )}
              />
              )}

              {/* Notes */}
              <TextField
                {...(isMultiProduct ? multiProductForm.register('notes') : singleProductForm.register('notes'))}
                margin="normal"
                fullWidth
                label="Transfer Notes (Optional)"
                multiline
                rows={3}
                helperText="Add any notes about this transfer"
              />
            </Box>
          )}

          {/* Step 2: Add Products (Multi-Product) */}
          {activeStep === 1 && isMultiProduct && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Products to Transfer
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddProduct}
                  disabled={!fromLocationId}
                >
                  Add Product
                </Button>
              </Box>

              {!fromLocationId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Please select source location in the previous step to see available products.
                </Alert>
              )}

              {fields.length === 0 && fromLocationId && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No products added. Click "Add Product" to start.
                </Alert>
              )}

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Available</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, width: 80 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map((field, index) => {
                      const selectedProductId = multiProductForm.watch(`items.${index}.productId`);
                      const availableQty = getAvailableQuantity(selectedProductId);

                      return (
                        <TableRow key={field.id}>
                          <TableCell>
                            <Controller
                              name={`items.${index}.productId`}
                              control={multiProductForm.control}
                              rules={{ required: 'Product is required' }}
                              render={({ field: prodField }) => (
                                <Autocomplete
                                  {...prodField}
                                  options={availableProducts}
                                  getOptionLabel={(option) =>
                                    typeof option === 'string'
                                      ? products.find((p) => p.id === option)?.name || ''
                                      : option.name
                                  }
                                  value={products.find((p) => p.id === prodField.value) || null}
                                  onChange={(_, newValue) => {
                                    prodField.onChange(newValue?.id || '');
                                  }}
                                  loading={productsLoading}
                                  size="small"
                                  renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                      {option.name}
                                    </li>
                                  )}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select product"
                                      error={!!multiProductForm.formState.errors.items?.[index]?.productId}
                                      size="small"
                                      InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                          <>
                                            {productsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                          </>
                                        ),
                                      }}
                                    />
                                  )}
                                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={selectedProductId ? availableQty : '-'}
                              size="small"
                              color={availableQty > 0 ? 'success' : 'default'}
                              sx={{ minWidth: 50 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              {...multiProductForm.register(`items.${index}.quantity`, {
                                valueAsNumber: true,
                                required: 'Quantity is required',
                                min: { value: 1, message: 'Min 1' },
                                max: { value: availableQty || Infinity, message: `Max ${availableQty}` },
                              })}
                              type="number"
                              size="small"
                              inputProps={{ min: 1, max: availableQty || undefined }}
                              error={!!multiProductForm.formState.errors.items?.[index]?.quantity}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              {...multiProductForm.register(`items.${index}.notes`)}
                              size="small"
                              placeholder="Optional notes"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveProduct(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Step 2: Add Product (Single-Product) */}
          {activeStep === 1 && !isMultiProduct && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Product Details
              </Typography>
              <Divider sx={{ mb: 3 }} />

              {!fromLocationId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Please select source location in the previous step.
                </Alert>
              )}

              {/* Product Selection */}
              <Controller
                name="productId"
                control={singleProductForm.control}
                rules={{ required: 'Product is required' }}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={availableProducts}
                    getOptionLabel={(option) =>
                      typeof option === 'string'
                        ? products.find((p) => p.id === option)?.name || ''
                        : option.name
                    }
                    value={products.find((p) => p.id === field.value) || null}
                    onChange={(_, newValue) => {
                      field.onChange(newValue?.id || '');
                    }}
                    loading={productsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product"
                        error={!!singleProductForm.formState.errors.productId}
                        helperText={singleProductForm.formState.errors.productId?.message}
                        margin="normal"
                        fullWidth
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {productsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                  />
                )}
              />

              {/* Quantity */}
              <TextField
                {...singleProductForm.register('quantity', {
                  valueAsNumber: true,
                  required: 'Quantity is required',
                  min: { value: 1, message: 'Quantity must be at least 1' },
                  max: {
                    value: getAvailableQuantity(singleProductForm.watch('productId')) || Infinity,
                    message: `Cannot exceed available quantity (${getAvailableQuantity(singleProductForm.watch('productId'))})`,
                  },
                })}
                error={!!singleProductForm.formState.errors.quantity}
                helperText={
                  singleProductForm.formState.errors.quantity?.message ||
                  `Available: ${getAvailableQuantity(singleProductForm.watch('productId'))}`
                }
                margin="normal"
                fullWidth
                label="Quantity"
                type="number"
                inputProps={{
                  min: 1,
                  max: getAvailableQuantity(singleProductForm.watch('productId')) || undefined,
                }}
              />
            </Box>
          )}

          {/* Step 3: Review */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Review Transfer
              </Typography>
              <Divider sx={{ mb: 3 }} />

              {/* Transfer Summary */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Transfer Details
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">From</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {`${fromLocations.find(l => l.id === fromLocationId)?.name || 'N/A'} (${fromLocationType})`}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">To</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {`${toLocations.find(l => l.id === (isMultiProduct ? multiProductForm.getValues('toLocationId') : singleProductForm.getValues('toLocationId')))?.name || 'N/A'} (${toLocationType})`}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Transfer Type</Typography>
                    <Chip
                      label={isMultiProduct ? 'Multi-Product' : 'Single Product'}
                      size="small"
                      color={isMultiProduct ? 'primary' : 'default'}
                      icon={isMultiProduct ? <Inventory2Icon /> : undefined}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Total Items</Typography>
                    <Typography variant="h6" color="primary">{getTotalItems()}</Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Products Table */}
              <Typography variant="subtitle2" gutterBottom>
                Products ({getTotalQuantity()} total units)
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isMultiProduct ? (
                      multiProductForm.getValues('items').map((item, index) => {
                        const product = products.find(p => p.id === item.productId);
                        return (
                          <TableRow key={index}>
                            <TableCell>{product?.name || 'Unknown'}</TableCell>
                            <TableCell align="center">
                              <Chip label={item.quantity} size="small" color="primary" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {item.notes || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell>
                          {products.find(p => p.id === singleProductForm.getValues('productId'))?.name || 'Unknown'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={singleProductForm.getValues('quantity')} size="small" color="primary" />
                        </TableCell>
                        <TableCell>-</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Transfer Notes */}
              {(() => {
                const notes = isMultiProduct ? multiProductForm.getValues('notes') : singleProductForm.getValues('notes');
                return notes ? (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">Transfer Notes</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {notes}
                    </Typography>
                  </Paper>
                ) : null;
              })()}
            </Box>
          )}

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={activeStep === 0 ? () => navigate('/app/stock-transfers') : handleBack}
              startIcon={activeStep === 0 ? <CancelIcon /> : <ArrowBackIcon />}
            >
              {activeStep === 0 ? 'Cancel' : 'Back'}
            </Button>

            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                startIcon={<SaveIcon />}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create Transfer'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
